import { gql } from '@apollo/client';

import { ASSESSMENT_SESSION_SUMMARY_FIELDS } from './assessment-session.queries';
import { COMMUNICATION_FIELDS } from './email-log.queries';

export const SEND_AD_HOC_COMMUNICATION = gql`
  mutation SendAdHocCommunication($input: AdHocCommunicationInput!) {
    sendAdHocCommunication(input: $input) {
      sent
      skipped
      failed
      queued
      communicationGroupId
    }
  }
`;

export const COUNT_AD_HOC_RECIPIENTS = gql`
  query CountAdHocRecipients(
    $assessmentId: ID!
    $criteria: AudienceCriteria!
    $deliveryOption: DeliveryOption!
    $contextType: CommunicationContextType
    $contextId: ID
  ) {
    countAdHocRecipients(
      assessmentId: $assessmentId
      criteria: $criteria
      deliveryOption: $deliveryOption
      contextType: $contextType
      contextId: $contextId
    ) {
      recipients
      messages
      unreachable
    }
  }
`;

// Registry is static per deploy — consumers read it cache-first (useAvailableVariables).
export const GET_AVAILABLE_VARIABLES = gql`
  query GetAvailableVariables($assessmentId: ID!) {
    availableVariables(assessmentId: $assessmentId) {
      key
      label
      example
      targetType
      receiverType
      requiresFallback
    }
  }
`;

export const GET_AD_HOC_RECIPIENTS = gql`
  query GetAdHocRecipients(
    $assessmentId: ID!
    $criteria: AudienceCriteria!
    $contextType: CommunicationContextType
    $contextId: ID
    $search: String
    $offset: Int
    $limit: Int
  ) {
    adHocRecipients(
      assessmentId: $assessmentId
      criteria: $criteria
      contextType: $contextType
      contextId: $contextId
      search: $search
      offset: $offset
      limit: $limit
    ) {
      items {
        playerAssessmentId
        playerName
        ageGroupName
      }
      total
    }
  }
`;

export const GET_AD_HOC_CONTEXT_VALUES = gql`
  query GetAdHocContextValues($assessmentId: ID!) {
    adHocContextValues(assessmentId: $assessmentId) {
      levels
      teams
    }
  }
`;

// One shared, debounced call powers token validation AND the preview panel —
// substitution is server-only, so the client renders this verbatim.
export const GET_PREVIEW_AD_HOC_COMMUNICATION = gql`
  query GetPreviewAdHocCommunication(
    $assessmentId: ID!
    $criteria: AudienceCriteria!
    $contextType: CommunicationContextType
    $contextId: ID
    $content: AdHocContentInput!
    $playerAssessmentId: ID
  ) {
    previewAdHocCommunication(
      assessmentId: $assessmentId
      criteria: $criteria
      contextType: $contextType
      contextId: $contextId
      content: $content
      playerAssessmentId: $playerAssessmentId
    ) {
      playerAssessmentId
      playerName
      subject
      bodyHtml
      smsBody
      smsSegments {
        text
        issue
      }
      report {
        unknown
        missingFallback
        splitTokens
        blanks
        usesReceiverScopedTokens
      }
    }
  }
`;

// Picker-only selection — deliberately no attendance/player/drill aggregates
// (those make GetAssessmentSessionList expensive; see useComposeContextOptions).
export const GET_COMPOSE_CONTEXT_SESSIONS = gql`
  query GetComposeContextSessions($assessmentId: ID!, $ageGroupIds: [ID!]!) {
    assessmentSessions(assessmentId: $assessmentId, ageGroupIds: $ageGroupIds) {
      _id
      location
      area
      date
      start
      ageGroup {
        _id
        name
      }
    }
  }
`;

// Picker-only selection — deliberately omits GetAgeGroups' per-row numPlayers
// aggregate, and takes assessmentId explicitly (panel ≠ active assessment).
export const GET_COMPOSE_CONTEXT_AGE_GROUPS = gql`
  query GetComposeContextAgeGroups($assessmentId: ID!) {
    ageGroups(assessmentId: $assessmentId) {
      _id
      name
    }
  }
`;

export const GET_AD_HOC_COMMUNICATION_HISTORY = gql`
  query GetAdHocCommunicationHistory($assessmentId: ID!, $search: String, $includeTests: Boolean, $offset: Int, $limit: Int) {
    adHocCommunicationHistory(assessmentId: $assessmentId, search: $search, includeTests: $includeTests, offset: $offset, limit: $limit) {
      items {
        _id
        createdAt
        title
        subject
        sentCount
        failedCount
        skippedCount
        queuedCount
        pendingCount
        hasEmail
        hasSms
        isTest
        assessmentSession {
          ...AssessmentSessionSummaryFields
        }
      }
      total
    }
  }
  ${ASSESSMENT_SESSION_SUMMARY_FIELDS}
`;

export const GET_AD_HOC_SEND_EVENT_ITEMS = gql`
  query GetAdHocSendEventItems($assessmentId: ID!, $communicationGroupId: ID!, $search: String, $offset: Int, $limit: Int) {
    adHocSendEventItems(
      assessmentId: $assessmentId
      communicationGroupId: $communicationGroupId
      search: $search
      offset: $offset
      limit: $limit
    ) {
      items {
        ...CommunicationFields
        subject
        communicationGroupId
        attempts
      }
      total
    }
  }
  ${COMMUNICATION_FIELDS}
`;

export const RESEND_AD_HOC_SEND_EVENT_FAILED = gql`
  mutation ResendAdHocSendEventFailed($communicationGroupId: ID!) {
    resendAdHocSendEventFailed(communicationGroupId: $communicationGroupId) {
      sent
      skipped
      failed
    }
  }
`;
